import { useNavigationStore } from 'store';
import Button from '@mui/material/Button';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SortIcon } from '@/components/navigation/NavigationIntroScreen/components/Popup/Icons';
import { FAIcon } from '@packages/ui-components';

export const SortDirectionButton = ({
  setSortingValue,
}: {
  setSortingValue: Dispatch<SetStateAction<string>>;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const translation = useNavigationStore((state) => state.navigationTranslation);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const sortingValueOptions = useMemo(
    () => [
      {
        value: 'default',
        label: translation?.navDefault || 'Default',
        icon: <FAIcon icon="faSort" size={16} />,
      },
      {
        value: 'alphabetical',
        label: translation?.navAlphabetical || 'A-Z',
        icon: (
          <FAIcon
            icon="faArrowUpArrowDown"
            size={16}
            sx={{
              transform: 'rotateX(180deg)',
            }}
          />
        ),
      },
      {
        value: 'reverseAlphabetical',
        label: translation?.navReverseAlphabetical || 'Z-A',
        icon: <FAIcon icon="faArrowUpArrowDown" size={16} />,
      },
    ],
    [translation?.navAlphabetical, translation?.navDefault, translation?.navReverseAlphabetical],
  );

  return (
    <>
      <Button
        id="sort-button"
        aria-controls={open ? 'sorting-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="contained"
        disableElevation
        onClick={handleClick}
        sx={{
          width: '36px',
          height: '36px',
          borderRadius: '30px',
          border: open ? '1px solid #2055FF' : '1px solid #EAECF0',
          background: open ? 'rgba(32, 85, 255, 0.06)' : '#FFF',
          color: open ? '#2055FF' : '#6B6B6B',
          minWidth: '0px',
          '&:hover': {
            background: 'rgba(32, 85, 255, 0.06)',
            color: open ? '#2055FF' : '#000',
          },
          '&:focus': {
            border: '1px solid #2055FF',
            background: 'rgba(32, 85, 255, 0.06)',
          },
          '.MuiButton-startIcon': {
            margin: '0px',
          },
        }}
        startIcon={<SortIcon style={{ width: '20px', height: '20px' }} color="#6B6B6B" />}
      />
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        id="sorting-menu"
        sx={{
          '.MuiPopover-paper': {
            width: '203px',
            borderRadius: '8px',
            border: '1px solid #EAECF0',
            mt: '10px',
          },
        }}
        MenuListProps={{
          'aria-labelledby': 'sort-button',
          sx: {
            background: '#fff',
          },
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {sortingValueOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              setSortingValue(option.value);
              handleClose();
            }}
            sx={{
              display: 'inline-flex',
              justifyContent: 'flex-start',
              width: '100%',
              gap: '8px',
              ':hover': {
                background: 'rgba(32, 85, 255, 0.06)',
              },
            }}
          >
            {option.icon}
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
